import { LeadCard } from './LeadCard';
import { History, Search, CheckCircle2, XCircle, AlertTriangle, FileText } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';

export type status = 'completed' | 'active' | 'declined' | 'dispute' | 'cancelled' | 'other';

const LEADS_PER_PAGE = 10;

const normalizeStatus = (value: unknown): status => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'disputed') return 'dispute';
  if (normalized === 'decline' || normalized === 'rejected') return 'declined';
  if (normalized === 'canceled') return 'cancelled';
  if (['accepted', 'open', 'requested', 'pending', 'in_progress'].includes(normalized)) return 'active';
  if (['completed', 'declined', 'dispute', 'cancelled'].includes(normalized)) {
    return normalized as status;
  }
  return 'other';
};



interface LeadHistoryProps {
  leads: any[];
  onViewDetails: (leadId: string) => void;
  isLoading?: boolean;
}

export function LeadHistory({ leads, onViewDetails, isLoading = false }: LeadHistoryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<'all' | status>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const terminalStatuses: status[] = ['completed', 'declined', 'dispute', 'cancelled'];
  const historyLeads = leads.filter((lead) => terminalStatuses.includes(normalizeStatus(lead.status)));
  // Filter leads
  const filteredLeads = historyLeads.filter(lead => {
    const matchesSearch = 
      lead.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.location.city.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || lead.category === categoryFilter;
    const matchesStatus = statusFilter === 'all' || normalizeStatus(lead.status) === statusFilter;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Sort leads
  const sortedLeads = [...filteredLeads].sort((a, b) => {
    const getRelevantDate = (lead: any) => {
      return lead.completedDate || lead.declinedDate || lead.disputedDate || lead.cancelledDate || lead.postedDate;
    };

    switch (sortBy) {
      case 'newest':
        return new Date(getRelevantDate(b)).getTime() - new Date(getRelevantDate(a)).getTime();
      case 'oldest':
        return new Date(getRelevantDate(a)).getTime() - new Date(getRelevantDate(b)).getTime();
      case 'budget-high':
        return b.budget - a.budget;
      case 'budget-low':
        return a.budget - b.budget
      default:
        return 0;
    }
  });

  const totalPages = Math.max(1, Math.ceil(sortedLeads.length / LEADS_PER_PAGE));
  const activePage = Math.min(currentPage, totalPages);
  const firstLeadIndex = (activePage - 1) * LEADS_PER_PAGE;
  const paginatedLeads = sortedLeads.slice(firstLeadIndex, firstLeadIndex + LEADS_PER_PAGE);
  const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortBy, categoryFilter, statusFilter]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const categories = ['all', ...Array.from(new Set(historyLeads.map(lead => lead.category).filter(Boolean)))];

  const getStatusCounts = () => {
    return {
      all: historyLeads.length,
      completed: historyLeads.filter(l => normalizeStatus(l.status) === 'completed').length,
      declined: historyLeads.filter(l => normalizeStatus(l.status) === 'declined').length,
      dispute: historyLeads.filter(l => normalizeStatus(l.status) === 'dispute').length,
      cancelled: historyLeads.filter(l => normalizeStatus(l.status) === 'cancelled').length,
    };
  };

  const statusCounts = getStatusCounts();
  const totalCompleted = statusCounts.completed;
  const totalValue = historyLeads
    .filter(l => normalizeStatus(l.status) === 'completed')
    .reduce((sum, lead) => sum + lead.budget, 0);

  return (
    <div className="lead-history-view lead-management-content">
      <div className="">
        {/* Stats */}
        {historyLeads.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 ">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <History className="size-6 text-purple-700" />
                </div>
                <div>
                  <div className="text-muted-foreground">Total History</div>
                  <div className="text-2xl">{historyLeads.length}</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle2 className="size-6 text-green-700" />
                </div>
                <div>
                  <div className="text-muted-foreground">Completed</div>
                  <div className="text-2xl">{totalCompleted}</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FileText className="size-6 text-blue-700" />
                </div>
                <div>
                  <div className="text-muted-foreground">Total Earned</div>
                  <div className="text-2xl">${totalValue.toLocaleString()}</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <AlertTriangle className="size-6 text-orange-700" />
                </div>
                <div>
                  <div className="text-muted-foreground">Disputes</div>
                  <div className="text-2xl">{statusCounts.dispute}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Status Filter Tabs */}
        {historyLeads.length > 0 && (
          <div className="lead-history-tabs mb-4 overflow-x-auto">
            <Tabs value={statusFilter} onValueChange={(value) => setStatusFilter(value as 'all' | status)}>
              <TabsList className="inline-flex w-fit max-w-full justify-start rounded-lg border border-gray-200 bg-white h-auto p-1 mb-0">
                <TabsTrigger 
                  value="all"
                  className="rounded-md px-4 py-2.5"
                >
                  All History
                  <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs">
                    {statusCounts.all}
                  </span>
                </TabsTrigger>
                <TabsTrigger 
                  value="completed"
                  className="rounded-md px-4 py-2.5"
                >
                  <CheckCircle2 className="size-4 mr-2" />
                  Completed
                  <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">
                    {statusCounts.completed}
                  </span>
                </TabsTrigger>
                <TabsTrigger 
                  value="declined"
                  className="rounded-md px-4 py-2.5"
                >
                  <XCircle className="size-4 mr-2" />
                  Declined
                  <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs">
                    {statusCounts.declined}
                  </span>
                </TabsTrigger>
                <TabsTrigger 
                  value="dispute"
                  className="rounded-md px-4 py-2.5"
                >
                  <AlertTriangle className="size-4 mr-2" />
                  Disputed
                  <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs">
                    {statusCounts.dispute}
                  </span>
                </TabsTrigger>
                <TabsTrigger 
                  value="cancelled"
                  className="rounded-md px-4 py-2.5"
                >
                  <XCircle className="size-4 mr-2" />
                  Cancelled
                  <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs">
                    {statusCounts.cancelled}
                  </span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4 border border-gray-100">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="app-search-field flex-1">
              <Search className="app-field-icon" />
              <Input
                type="text"
                placeholder="Search lead history..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex gap-3">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                  <SelectItem value="budget-high">Highest Budget</SelectItem>
                  <SelectItem value="budget-low">Lowest Budget</SelectItem>
                </SelectContent>
              </Select>

              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category === 'all' ? 'All Categories' : category.charAt(0).toUpperCase() + category.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {(searchQuery || categoryFilter !== 'all') && (
            <div className="flex items-center gap-2 mt-4">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {searchQuery && (
                <Badge variant="secondary" className="gap-1">
                  Search: {searchQuery}
                  <button onClick={() => setSearchQuery('')} className="ml-1 hover:text-destructive">
                    ×
                  </button>
                </Badge>
              )}
              {categoryFilter !== 'all' && (
                <Badge variant="secondary" className="gap-1">
                  Category: {categoryFilter}
                  <button onClick={() => setCategoryFilter('all')} className="ml-1 hover:text-destructive">
                    ×
                  </button>
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Leads Grid */}
        <div className="mb-3 text-sm text-muted-foreground">
          {sortedLeads.length > 0
            ? `Showing ${firstLeadIndex + 1}-${Math.min(firstLeadIndex + LEADS_PER_PAGE, sortedLeads.length)} of ${sortedLeads.length} history records`
            : 'Showing 0 history records'}
        </div>
        {sortedLeads.length > 0 ? (
          <div className="grid grid-cols-1 gap-4">
            {paginatedLeads.map((lead) => (
                <div key={lead.id} className="relative">
                  <LeadCard
                    lead={lead}
                    onViewDetails={onViewDetails}
                    showActionButtons={false}
                    compact
                  />
                  
                  {/* Additional Info Footer */}
                  {lead.notes && (
                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                      <p className="text-sm text-muted-foreground">
                        <strong>Notes:</strong> {lead.notes}
                      </p>
                    </div>
                  )}
                </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            {historyLeads.length === 0 ? (
              <div>
                <History className="size-16 text-gray-300 mx-auto mb-4" />
                <p className="text-muted-foreground mb-2">
                  No lead history yet.
                </p>
                <p className="text-muted-foreground">
                  Your completed, declined, and disputed leads will appear here.
                </p>
              </div>
            ) : (
              <p className="text-muted-foreground">
                No leads found matching your criteria. Try adjusting your filters.
              </p>
            )}
          </div>
        )}

        <nav className="mt-8 flex flex-col items-center justify-between gap-4 border-t pt-6 sm:flex-row" aria-label="Lead history pagination">
          <p className="text-sm text-muted-foreground">
            Page {activePage} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={activePage === 1}
            >
              Previous
            </Button>
            <div className="flex items-center gap-1" aria-label="Page numbers">
              {pageNumbers.map((page) => (
                <Button
                  key={page}
                  variant={page === activePage ? 'default' : 'outline'}
                  size="sm"
                  className="min-w-8 px-2"
                  aria-current={page === activePage ? 'page' : undefined}
                  aria-label={`Go to page ${page}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={activePage === totalPages}
            >
              Next
            </Button>
          </div>
        </nav>
      </div>
    </div>
  );
}
